"use client";

import React, { useMemo } from "react";
import type { EChartsOption } from "echarts";
import { BaseChart, type BaseChartRef } from "./base-chart";
import {
  chartTooltip,
  chartLegend,
  chartGrid,
  chartCategoryAxis,
  chartValueAxis,
  chartTitle,
  chartAnimation,
  chartColors,
  chartColor,
} from "./chart-utils";
import { cn } from "@/src/utils/utils";

/** Convert hex (#rrggbb) to rgba string with given alpha. */
function hexToRgba(hex: string, alpha: number): string {
  const match = hex.match(/^#([0-9a-fA-F]{2})([0-9a-fA-F]{2})([0-9a-fA-F]{2})$/);
  if (match) {
    return `rgba(${parseInt(match[1], 16)}, ${parseInt(match[2], 16)}, ${parseInt(match[3], 16)}, ${alpha})`;
  }
  return hex;
}

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface LineChartSeries {
  /** Series name — shown in legend and tooltip */
  name: string;
  /** Data points for this series */
  data: number[];
  /** Optional override color (hex or CSS color) */
  color?: string;
  /** If true, render as dashed line. Default false */
  dashed?: boolean;
  /** If true, fill area under this line with gradient. Default false */
  showArea?: boolean;
}

export interface LineChartData {
  /** Category labels for X-axis */
  categories: string[];
  /** One or more data series */
  series: LineChartSeries[];
}

export interface LineChartProps {
  /** Data structure: categories + series */
  data: LineChartData;
  /** Chart title (optional) */
  title?: string;
  /** Chart subtitle (optional) */
  subtitle?: string;
  /** Chart height — CSS value or number (px). Default "100%" */
  height?: string | number;
  /** Extra class names for the wrapper */
  className?: string;
  /** If true, use smooth curves. Default true */
  smooth?: boolean;
  /** If true, fill area under all lines. Default false */
  showArea?: boolean;
  /** If true, show legend. Default true */
  showLegend?: boolean;
  /** If true, show data point symbols. Default false */
  showSymbol?: boolean;
  /** If true, show loading spinner overlay */
  loading?: boolean;
  /** If true, show empty state. Overrides data if both are true */
  empty?: boolean;
  /** Custom empty state message */
  emptyText?: string;
  /** Called when a data point is clicked: { name, seriesName, value } */
  onPointClick?: (params: {
    name: string;
    seriesName: string;
    value: number;
  }) => void;
  /** Optional value formatter (e.g., for currency or percentages) */
  formatValue?: (value: number) => string;
  /** Accessible label for screen readers */
  ariaLabel?: string;
}

// ---------------------------------------------------------------------------
// LineChart Component
// ---------------------------------------------------------------------------

export const LineChart = React.forwardRef<BaseChartRef, LineChartProps>(
  function LineChart(
    {
      data,
      title,
      subtitle,
      height = "100%",
      className,
      smooth = true,
      showArea = false,
      showLegend = true,
      showSymbol = false,
      loading = false,
      empty = false,
      emptyText,
      onPointClick,
      formatValue,
      ariaLabel,
    },
    ref
  ) {
    // Build the ECharts option
    const option = useMemo<EChartsOption>(() => {
      const { categories, series } = data;

      // Build series with colors and styling
      const chartSeries = series.map((s, idx) => {
        const seriesColor = s.color ?? chartColor(idx);

        return {
          type: "line" as const,
          name: s.name,
          data: s.data,
          smooth: smooth,
          symbol: showSymbol ? "circle" : "none",
          symbolSize: 6,
          lineStyle: {
            color: seriesColor,
            width: 2,
            type: s.dashed ? "dashed" as const : "solid" as const,
          },
          itemStyle: {
            color: seriesColor,
            borderColor: "#fff",
            borderWidth: 2,
          },
          emphasis: {
            focus: "series" as const,
            blurScope: "coordinateSystem" as const,
            lineStyle: { width: 3.5 },
            itemStyle: {
              borderWidth: 3,
              borderColor: "#fff",
              shadowBlur: 8,
              shadowColor: "rgba(0, 0, 0, 0.18)",
            },
          },
          // Area fill
          areaStyle: s.showArea || showArea
            ? {
                color: {
                  type: "linear",
                  x: 0,
                  y: 0,
                  x2: 0,
                  y2: 1,
                  colorStops: [
                    { offset: 0, color: hexToRgba(seriesColor, 0.4) },
                    { offset: 1, color: hexToRgba(seriesColor, 0) },
                  ],
                },
              }
            : undefined,
          // Animation on line draw
          animationDuration: 1200,
          animationEasing: "cubicInOut",
          animationDelay: (idx: number) => idx * 60,
        };
      });

      // Build base option
      let baseOption: Record<string, any> = {
        ...(title && chartTitle(title, { subtitle })),
        ...chartTooltip({
          trigger: "axis",
          formatter: (params: any) => {
            if (!Array.isArray(params)) params = [params];
            let html = `<strong>${params[0]?.name}</strong><br/>`;
            params.forEach((p: any) => {
              const val = formatValue ? formatValue(p.value) : p.value;
              html += `<span style="display:inline-block;margin-right:8px;"><span style="display:inline-block;width:8px;height:8px;border-radius:50%;background:${p.color};margin-right:6px;"></span>${p.seriesName}: ${val}</span><br/>`;
            });
            return html;
          },
        }),
        ...(showLegend && chartLegend({ position: "bottom" })),
        ...chartGrid(),
        ...chartAnimation({ delay: 0 }),
        xAxis: chartCategoryAxis(categories),
        yAxis: chartValueAxis(),
      };

      baseOption.series = chartSeries;

      return baseOption;
    }, [data, title, subtitle, smooth, showArea, showLegend, showSymbol, formatValue]);

    // Handle point click events
    const onEvents = useMemo(() => {
      if (!onPointClick) return undefined;
      return {
        click: (params: any) => {
          if (params.componentSubType === "line") {
            onPointClick({
              name: params.name,
              seriesName: params.seriesName,
              value: params.value,
            });
          }
        },
      };
    }, [onPointClick]);

    // Generate data summary for accessibility
    const dataSummary = useMemo(() => {
      const { categories, series } = data;
      const lines = series.map(s => {
        const points = s.data.map((val, i) => `${categories[i]}: ${formatValue ? formatValue(val) : val}`).join(", ");
        return `${s.name}: ${points}`;
      });
      return lines.join(". ");
    }, [data, formatValue]);

    return (
      <BaseChart
        ref={ref}
        option={option}
        height={height}
        className={cn("line-chart", className)}
        loading={loading}
        empty={empty}
        emptyText={emptyText}
        onEvents={onEvents}
        ariaLabel={ariaLabel || `Line chart${title ? `: ${title}` : ""} showing ${data.series.map(s => s.name).join(", ")}`}
        dataSummary={dataSummary}
      />
    );
  }
);

LineChart.displayName = "LineChart";

export default LineChart;
